import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Mail, Plus, Edit, Trash2, Eye, AlertCircle } from 'lucide-react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'other',
    html: false
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates`, {
        withCredentials: true
      });
      setTemplates(response.data.templates);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch templates');
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      body: '',
      category: 'other',
      html: false
    });
    setEditorState(EditorState.createEmpty());
    setShowDialog(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category,
      html: template.html
    });
    
    // Set editor state from HTML or plain text
    if (template.html && template.body) {
      const contentBlock = htmlToDraft(template.body);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        setEditorState(EditorState.createWithContent(contentState));
      }
    } else {
      const contentState = ContentState.createFromText(template.body || '');
      setEditorState(EditorState.createWithContent(contentState));
    }
    
    setShowDialog(true);
  };

  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
    
    // Update formData based on HTML mode
    if (formData.html) {
      const rawContentState = convertToRaw(newEditorState.getCurrentContent());
      const htmlContent = draftToHtml(rawContentState);
      setFormData({...formData, body: htmlContent});
    } else {
      const plainText = newEditorState.getCurrentContent().getPlainText();
      setFormData({...formData, body: plainText});
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(
          `${API}/email-templates/${editingTemplate.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Template updated successfully');
      } else {
        await axios.post(
          `${API}/email-templates`,
          formData,
          { withCredentials: true }
        );
        toast.success('Template created successfully');
      }
      
      setShowDialog(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/email-templates/${templateId}`, {
        withCredentials: true
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const categoryColors = {
    follow_up: 'bg-blue-100 text-blue-700',
    introduction: 'bg-green-100 text-green-700',
    proposal: 'bg-purple-100 text-purple-700',
    thank_you: 'bg-pink-100 text-pink-700',
    other: 'bg-slate-100 text-slate-700'
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Email Templates</h1>
          <p className="text-slate-600 mt-2">Create and manage email templates with merge fields</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Merge Fields Help */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Available Merge Fields
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{'{{name}}'} - Lead name</Badge>
            <Badge variant="outline">{'{{email}}'} - Lead email</Badge>
            <Badge variant="outline">{'{{company}}'} - Company name</Badge>
            <Badge variant="outline">{'{{phone}}'} - Lead phone</Badge>
          </div>
          <p className="text-sm text-slate-600 mt-2">
            Use these placeholders in your subject and body. They'll be replaced with actual lead data when composing emails.
          </p>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell className="font-medium">{template.name}</TableCell>
                  <TableCell>
                    <Badge className={categoryColors[template.category]}>
                      {template.category.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-md truncate">{template.subject}</TableCell>
                  <TableCell>{new Date(template.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handlePreview(template)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(template.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {templates.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No email templates yet. Create your first one!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Email Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Follow-up Email"
              />
            </div>
            
            <div>
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="introduction">Introduction</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="thank_you">Thank You</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Subject *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder="e.g., Following up on our conversation, {{name}}"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.html}
                onCheckedChange={(checked) => {
                  setFormData({...formData, html: checked});
                  // Convert editor content when switching modes
                  if (checked) {
                    const rawContentState = convertToRaw(editorState.getCurrentContent());
                    const htmlContent = draftToHtml(rawContentState);
                    setFormData(prev => ({...prev, body: htmlContent, html: true}));
                  } else {
                    const plainText = editorState.getCurrentContent().getPlainText();
                    setFormData(prev => ({...prev, body: plainText, html: false}));
                  }
                }}
              />
              <Label>Enable Rich Text (HTML) Formatting</Label>
            </div>

            <div>
              <Label>Email Body *</Label>
              <div className="border rounded-md mt-1" style={{ minHeight: '300px' }}>
                <Editor
                  editorState={editorState}
                  onEditorStateChange={onEditorStateChange}
                  toolbar={{
                    options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'history'],
                    inline: { options: ['bold', 'italic', 'underline'] },
                    blockType: { inDropdown: true, options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'] },
                    fontSize: { options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36] },
                    list: { options: ['unordered', 'ordered'] },
                  }}
                  placeholder="Hi {{name}}, I wanted to follow up..."
                  editorStyle={{
                    minHeight: '250px',
                    padding: '10px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Tip: Use merge fields like {'{{name}}'}, {'{{email}}'}, {'{{company}}'}, {'{{phone}}'} in your template
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Subject:</Label>
              <p className="mt-1 p-3 bg-slate-50 rounded border">{previewTemplate?.subject}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Body:</Label>
              <div className="mt-1 p-3 bg-slate-50 rounded border whitespace-pre-wrap">
                {previewTemplate?.body}
              </div>
            </div>
            <div>
              <Badge className={categoryColors[previewTemplate?.category]}>
                {previewTemplate?.category.replace('_', ' ')}
              </Badge>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesPage;